/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Download, Filter, Search, Eye, Users, UserCheck, UserX, ExternalLink, AlertTriangle, TrendingUp, ChevronLeft, ChevronRight } from "lucide-react"
import { Link } from "react-router-dom"
import type { GeneralDiary, SuspectDetail, VictimDetail } from "@/lib/interfaces"
import { SuspectDetailPopup } from "@/components/suspect-detail-popup"
import bgImage from "@/assets/favicon.png"
import apiService from "@/services/apiService"
import { useAppStore } from "@/store/appStore"
import { getUserInfo } from "@/services/userService"

interface PersonRecord {
  id: string
  name: string
  fatherName: string
  type: "SUSPECT" | "VICTIM"
  gdNumber: string
  date: string
  caseCategory: string
  sections: string[]
  policeStation: any
  officerName: string
  status: "ACTIVE" | "RESOLVED" | "PENDING",
  generalDiaries?: GeneralDiary[]
}

interface TopSuspectsResponse {
  total: number
  suspects: SuspectDetail[]
}

interface TopVictimsResponse {
  total: number
  victims: VictimDetail[]
}

interface PaginationData {
  totalPages: number
  startIndex: number
  endIndex: number
  total: number
  currentPage: number
  loading: boolean
}

export default function SuspectsVictimsReport() {
  const { _filterOptions } = useAppStore();
  
  // Separate data states for suspects and victims
  const [filteredSuspectsData, setFilteredSuspectsData] = useState<PersonRecord[]>([])
  const [filteredVictimsData, setFilteredVictimsData] = useState<PersonRecord[]>([])
  
  const [topSuspects, setTopSuspects] = useState<SuspectDetail[]>([])
  const [topVictims, setTopVictims] = useState<VictimDetail[]>([])
  const [selectedSuspect, setSelectedSuspect] = useState<SuspectDetail | null>(null)
  const [suspectDetailOpen, setSuspectDetailOpen] = useState(false)
  const [topSuspectsLoading, setTopSuspectsLoading] = useState(false)
  const [topVictimsLoading, setTopVictimsLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)

  const userInfo:any = getUserInfo();
  const userId = userInfo?(userInfo.id?userInfo.id:null):null;
    
  
  // Separate pagination states for suspects and victims
  const [suspectsPagination, setSuspectsPagination] = useState<PaginationData>({
    totalPages: 0,
    startIndex: 0,
    endIndex: 0,
    total: 0,
    currentPage: 1,
    loading: false
  })
  
  const [victimsPagination, setVictimsPagination] = useState<PaginationData>({
    totalPages: 0,
    startIndex: 0,
    endIndex: 0,
    total: 0,
    currentPage: 1,
    loading: false
  })
  
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    policeStation: "",
    type: "",
    status: "",
    caseCategory: "",
    gdType: "",
    riskLevel: "",
    officerName: "",
    actSection: "",
    address: "",
  })
  const [topLimit, setTopLimit] = useState(10)
  const [minCount, setMinCount] = useState<number | "">("")
  const [searchTerm, setSearchTerm] = useState("")
  const [activeTab, setActiveTab] = useState("top-suspects")
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [loadedTabs, setLoadedTabs] = useState<Record<string, boolean>>({})
  const [stats, setStats] = useState({ suspects: 0, victims: 0, activeCases: 0 })

  const getStats = async () => {
    try {
      const queryParams: any = {}
      if (userId) queryParams["user_id"] = userId;
      Object.entries(filters).forEach(([key, value]) => {
        if (typeof value === "string" && value.trim() && value.trim() !== 'unknown') {
          queryParams[key] = value;
        }
      });
      if (searchTerm.trim()) queryParams.search = searchTerm;
      const json: any = await apiService.get('general-diaries/suspects-victims-stats', queryParams, {});
      setStats({ suspects: json.suspects || 0, victims: json.victims || 0, activeCases: json.activeCases || 0 });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }

  const fetchForTab = (tab: string) => {
    if (tab === "top-suspects") getTopSuspects();
    else if (tab === "top-victims") getTopVictims();
    else if (tab === "suspect") getSuspects(itemsPerPage, 1);
    else if (tab === "victim") getVictims(itemsPerPage, 1);
  };

  useEffect(() => {
    setItemsPerPage(10);
    fetchForTab(activeTab);
    setLoadedTabs({ [activeTab]: true });
    getStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (activeTab === "top-suspects" || activeTab === "top-victims") {
      fetchForTab(activeTab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topLimit, minCount])

  // Separate function to fetch suspects
  const getSuspects = async (limit: number = 10, page: number = 1) => {
    setSuspectsPagination(prev => ({ ...prev, loading: true }));
    try {
      const queryParams: any = { limit, page };
      Object.entries(filters).forEach(([key, value]) => {
        if (key === "questionerResponses" && typeof value === "object" && value !== null) {
          Object.entries(value).forEach(([questionId, response]) => {
            if (response !== null) {
              queryParams[`questioner_${questionId}`] = response;
            }
          });
        } else if (Array.isArray(value) && value.length > 0 && value[0] !== 'unknown') {
          queryParams[key] = value.join(",");
        } else if (typeof value === "string" && value.trim() && value.trim() !== 'unknown') {
          queryParams[key] = value;
        } else if (typeof value === "boolean") {
          queryParams[key] = value;
        }
      });
      if (searchTerm.trim()) {
        queryParams.search = searchTerm;
      } 
      if (userId) queryParams["user_id"] = userId;
      const jsonSuspects: any = await apiService.get('general-diaries/suspects', queryParams, {});
      const suspectsDataList: PersonRecord[] = jsonSuspects.data.map((data: any, index: number) => ({
        id: `suspect-${page}-${index}`,
        name: data.name,
        fatherName: data.fatherName,
        type: "SUSPECT",
        gdNumber: data.generalDiary?.gdNo,
        date: data.createdAt,
        caseCategory: data.generalDiary?.categories[0]?.caseCategory?.title ?? "",
        sections: [],
        policeStation: data.generalDiary?.policeStation,
        officerName: data.generalDiary?.reportPrintedByName,
        status: "RESOLVED",
        generalDiaries: [data.generalDiary]
      }));
      
      setFilteredSuspectsData(suspectsDataList);
      setSuspectsPagination({
        totalPages: jsonSuspects.pagination.totalPages,
        startIndex: jsonSuspects.pagination.limit * (jsonSuspects.pagination.page - 1),
        endIndex: jsonSuspects.pagination.limit * jsonSuspects.pagination.page,
        total: jsonSuspects.pagination.total,
        currentPage: page,
        loading: false
      });
    } catch (error) {
      console.error("Error fetching suspects:", error);
      setSuspectsPagination(prev => ({ ...prev, loading: false }));
    }
  }

  // Separate function to fetch victims
  const getVictims = async (limit: number = 10, page: number = 1) => {
    setVictimsPagination(prev => ({ ...prev, loading: true }));
    try {
      const queryParams: any = { limit, page };
      Object.entries(filters).forEach(([key, value]) => {
        if (key === "questionerResponses" && typeof value === "object" && value !== null) {
          Object.entries(value).forEach(([questionId, response]) => {
            if (response !== null) {
              queryParams[`questioner_${questionId}`] = response;
            }
          });
        } else if (Array.isArray(value) && value.length > 0 && value[0] !== 'unknown') {
          queryParams[key] = value.join(",");
        } else if (typeof value === "string" && value.trim() && value.trim() !== 'unknown') {
          queryParams[key] = value;
        } else if (typeof value === "boolean") {
          queryParams[key] = value;
        }
      });
      if (searchTerm.trim()) {
        queryParams.search = searchTerm;
      }
      if (userId) queryParams["user_id"] = userId;
      const jsonVictims: any = await apiService.get('general-diaries/victims', queryParams, {});
      const victimsDataList: PersonRecord[] = jsonVictims.data.map((data: any, index: number) => ({
        id: `victim-${page}-${index}`,
        name: data.name,
        fatherName: data.fatherName,
        type: "VICTIM",
        gdNumber: data.generalDiary?.gdNo,
        date: data.createdAt,
        caseCategory: data.generalDiary?.categories[0]?.caseCategory?.title ?? "",
        sections: [],
        policeStation: data.generalDiary?.policeStation,
        officerName: data.generalDiary?.reportPrintedByName,
        status: "RESOLVED",
        generalDiaries: [data.generalDiary]
      }));
      
      setFilteredVictimsData(victimsDataList);
      setVictimsPagination({
        totalPages: jsonVictims.pagination.totalPages,
        startIndex: jsonVictims.pagination.limit * (jsonVictims.pagination.page - 1),
        endIndex: jsonVictims.pagination.limit * jsonVictims.pagination.page,
        total: jsonVictims.pagination.total,
        currentPage: page,
        loading: false
      });
    } catch (error) {
      console.error("Error fetching victims:", error);
      setVictimsPagination(prev => ({ ...prev, loading: false }));
    }
  }

  // Handle page changes for suspects
  const handleSuspectsPageChange = (page: number) => {
    getSuspects(itemsPerPage, page);
  };

  // Handle page changes for victims
  const handleVictimsPageChange = (page: number) => {
    getVictims(itemsPerPage, page);
  };

  const buildTopQueryParams = (overrides: Record<string, any> = {}) => {
    const queryParams: any = {}
    if (userId) queryParams["user_id"] = userId;
    queryParams["limit"] = topLimit;
    if (typeof minCount === "number" && minCount > 0) queryParams["minCount"] = minCount;
    Object.entries(filters).forEach(([key, value]) => {
      if (typeof value === "string" && value.trim() && value.trim() !== 'unknown') {
        queryParams[key] = value;
      }
    });
    if (searchTerm.trim()) queryParams.search = searchTerm;
    return { ...queryParams, ...overrides };
  }

  const getTopSuspects = async () => {
    setTopSuspectsLoading(true)
    try {
      const jsonSuspects: TopSuspectsResponse = await apiService.get('general-diaries/top-suspects', buildTopQueryParams(), {});
      setTopSuspects(jsonSuspects.suspects)
    } catch (error) {
      console.error("Error fetching top suspects:", error);
    } finally {
      setTopSuspectsLoading(false)
    }
  }

  const getTopVictims = async () => {
    setTopVictimsLoading(true)
    try {
      const jsonVictims: TopVictimsResponse = await apiService.get('general-diaries/top-victims', buildTopQueryParams(), {});
      setTopVictims(jsonVictims.victims)
    } catch (error) {
      console.error("Error fetching top victims:", error);
    } finally {
      setTopVictimsLoading(false)
    }
  }

  const exportTopList = async (type: "suspects" | "victims") => {
    setExportLoading(true)
    try {
      const endpoint = type === "suspects" ? 'general-diaries/top-suspects' : 'general-diaries/top-victims';
      const json: any = await apiService.get(endpoint, buildTopQueryParams({ exportAll: true }), {});
      const rows = type === "suspects" ? (json.suspects || []) : (json.victims || []);

      const headers = [
        "Rank", "Name", "Father Name", "Case Count",
        "GD No", "GD Date", "GD Time", "GD Type", "Information Type", "Case Type", "Subject",
        "Under Section", "Officer Name", "Risk Level",
        "Police Station", "District", "Circle", "State",
        "Incident Village", "Action Taken", "Incident Description",
        "Report Printed On", "Report Printed By", "Report Printed By Rank",
        "Additional Details", "Notes"
      ];

      const esc = (v: any) => {
        const s = v === null || v === undefined ? "" : String(v);
        return `"${s.replace(/"/g, '""').replace(/\r?\n/g, " ")}"`;
      };

      const fmtDate = (d: any) => d ? new Date(d).toLocaleDateString('en-IN') : "";
      const fmtTime = (d: any) => d ? new Date(d).toLocaleTimeString('en-IN') : "";

      const csvRows: string[] = [headers.join(",")];

      rows.forEach((r: any, i: number) => {
        const gds = (r.generalDiaries || []);
        if (gds.length === 0) {
          csvRows.push([
            i + 1, esc(r.name), esc(r.fatherName), r.count,
            ...Array(headers.length - 4).fill('""'),
          ].join(","));
          return;
        }
        gds.forEach((g: any) => {
          csvRows.push([
            i + 1,
            esc(r.name),
            esc(r.fatherName),
            r.count,
            esc(g.gdNo),
            esc(fmtDate(g.gdDateTime)),
            esc(fmtTime(g.gdDateTime)),
            esc(g.gdType),
            esc(g.informationType),
            esc(g.caseType),
            esc(g.subject),
            esc(g.underSection),
            esc(g.officerName),
            esc(g.riskLevel),
            esc(g.policeStation?.name),
            esc(g.policeStation?.district || g.districtUnit),
            esc(g.policeStation?.circle),
            esc(g.state),
            esc(g.incidentVillage),
            esc(g.actionTaken),
            esc(g.incidentDescription),
            esc(fmtDate(g.reportPrintedOn)),
            esc(g.reportPrintedByName),
            esc(g.reportPrintedByRank),
            esc(g.additionalDetails),
            esc(g.notes),
          ].join(","));
        });
      });

      const blob = new Blob(["﻿" + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `top-${type}-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting top list:", error);
    } finally {
      setExportLoading(false)
    }
  }

  const applyFilters = () => {
    setSuspectsPagination(prev => ({ ...prev, currentPage: 1 }));
    setVictimsPagination(prev => ({ ...prev, currentPage: 1 }));
    setLoadedTabs({ [activeTab]: true });
    fetchForTab(activeTab);
    getStats();
  }

  const getPersonTypeBadge = (type: string) => {
    return type === "SUSPECT" ? (
      <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
        <UserX className="h-3 w-3 mr-1" />
        Suspect
      </Badge>
    ) : (
      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
        <UserCheck className="h-3 w-3 mr-1" />
        Victim
      </Badge>
    )
  }

  const getRiskLevelBadge = (caseCount: number) => {
    if (caseCount >= 3) {
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
          <AlertTriangle className="h-3 w-3 mr-1" />
          High Risk
        </Badge>
      )
    } else if (caseCount === 2) {
      return (
        <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Medium Risk
        </Badge>
      )
    } else {
      return (
        <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Low Risk
        </Badge>
      )
    }
  }

  const exportToCSV = async () => {
    setExportLoading(true)
    try {
      const queryParams: any = {}
      if (userId) queryParams["user_id"] = userId;
      Object.entries(filters).forEach(([key, value]) => {
        if (typeof value === "string" && value.trim() && value.trim() !== 'unknown') {
          queryParams[key] = value;
        }
      });
      if (searchTerm.trim()) queryParams.search = searchTerm;

      const [suspectsRes, victimsRes]: any[] = await Promise.all([
        apiService.get('general-diaries/suspects/export', queryParams, {}),
        apiService.get('general-diaries/victims/export', queryParams, {}),
      ]);

      const headers = [
        "Type", "Name", "Father Name", "Address",
        "GD Number", "GD Date", "GD Type", "Case Category",
        "Sections", "Risk Level", "Police Station", "Officer Name"
      ]

      const buildRow = (data: any, type: string) => {
        const gd = data.generalDiary || {}
        return [
          type,
          data.name || "",
          data.fatherName || "",
          (data.address || "").replace(/,/g, ";"),
          gd.gdNo || "",
          gd.gdDateTime ? new Date(gd.gdDateTime).toLocaleDateString('en-IN') : "",
          gd.gdType || "",
          (gd.categories || []).map((c: any) => c.caseCategory?.title).filter(Boolean).join("; "),
          (gd.actsAndSections || []).map((a: any) => `${a.act} ${a.section}`).join("; "),
          gd.riskLevel || "",
          gd.policeStation?.name || "",
          gd.officerName || "",
        ].join(",")
      }

      const rows = [
        headers.join(","),
        ...(suspectsRes.data || []).map((d: any) => buildRow(d, "SUSPECT")),
        ...(victimsRes.data || []).map((d: any) => buildRow(d, "VICTIM")),
      ]

      const blob = new Blob([rows.join("\n")], { type: "text/csv" })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `suspects-victims-full-${new Date().toISOString().split("T")[0]}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Error exporting:", error)
    } finally {
      setExportLoading(false)
    }
  }

  const handleViewSuspect = (suspect: SuspectDetail | PersonRecord) => {
    setSelectedSuspect(suspect as SuspectDetail)
    setSuspectDetailOpen(true)
  }

  // Render pagination component
  const renderPagination = (pagination: PaginationData, onPageChange: (page: number) => void) => {
    if (pagination.totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600">
          Showing {pagination.startIndex + 1} to {Math.min(pagination.endIndex, pagination.total)} of {pagination.total} records
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onPageChange(pagination.currentPage - 1)} 
            disabled={pagination.currentPage === 1 || pagination.loading} 
            className="border-gray-300"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>

          <div className="flex items-center gap-1">
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter((page) => {
                return page === 1 || page === pagination.totalPages || Math.abs(page - pagination.currentPage) <= 1;
              })
              .map((page, index, array) => (
                <div key={page} className="flex items-center">
                  {index > 0 && array[index - 1] !== page - 1 && <span className="px-2 text-gray-400">...</span>}
                  <Button 
                    variant={pagination.currentPage === page ? "default" : "outline"} 
                    size="sm" 
                    onClick={() => onPageChange(page)} 
                    disabled={pagination.loading}
                    className={pagination.currentPage === page ? "bg-black text-white" : "border-gray-300"}
                  >
                    {page}
                  </Button>
                </div>
              ))}
          </div>

          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onPageChange(pagination.currentPage + 1)} 
            disabled={pagination.currentPage === pagination.totalPages || pagination.loading} 
            className="border-gray-300"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (!loadedTabs[tab]) {
      fetchForTab(tab);
      setLoadedTabs(prev => ({ ...prev, [tab]: true }));
    }
  };

  // const suspectsCount = filteredSuspectsData.length
  // const victimsCount = filteredVictimsData.length
  const activeCount = stats.activeCases

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0d2846]/3 via-[#e2af58]/10 to-[#0d2846]/3 p-6">
      <div
        className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-3 pointer-events-none"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <Link to="/reports" className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block">
              ← Back to Reports
            </Link>
            <h1 className="text-3xl font-bold text-black">Suspects & Victims Report</h1>
            <p className="text-gray-600 mt-1">Track individuals involved across GDs with linking capabilities</p>
          </div>
          <Button
            onClick={exportToCSV}
            disabled={exportLoading}
            className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white"
          >
            <Download className="h-4 w-4 mr-2" />
            {exportLoading ? "Exporting..." : "Full Export CSV"}
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-gray-600" />
                <div>
                  <div className="text-2xl font-bold text-black">{stats.suspects + stats.victims}</div>
                  <p className="text-sm text-gray-600">Total Records</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <UserX className="h-5 w-5 text-red-600" />
                <div>
                  <div className="text-2xl font-bold text-red-600">{stats.suspects}</div>
                  <p className="text-sm text-gray-600">Total Suspects</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <UserCheck className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold text-blue-600">{stats.victims}</div>
                  <p className="text-sm text-gray-600">Total Victims</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <div className="h-5 w-5 bg-orange-500 rounded-full"></div>
                <div>
                  <div className="text-2xl font-bold text-orange-600">{activeCount}</div>
                  <p className="text-sm text-gray-600">Active Cases</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg text-black flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters & Search
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">From Date</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">To Date</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Police Station</Label>
                <Select
                  value={filters.policeStation}
                  onValueChange={(value) => setFilters({ ...filters, policeStation: value })}
                >
                  <SelectTrigger className="border-gray-300 w-full">
                    <SelectValue placeholder="Select station" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {_filterOptions?.policeStations?.map((station: any) => (
                      <SelectItem key={station.name} value={station.name}>
                        {station.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Case Category</Label>
                <Select value={filters.caseCategory} onValueChange={(value) => setFilters({ ...filters, caseCategory: value })}>
                  <SelectTrigger className="border-gray-300 w-full">
                    <SelectValue placeholder="Select case Category" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {_filterOptions?.caseCategories?.map((cat: any, index: number) => (
                      <SelectItem key={cat.title + index} value={cat.title}>
                        {cat.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">GD Type</Label>
                <Select value={filters.gdType || "all"} onValueChange={(value) => setFilters({ ...filters, gdType: value === "all" ? "" : value })}>
                  <SelectTrigger className="border-gray-300 w-full">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    <SelectItem value="all">All Types</SelectItem>
                    {_filterOptions?.gdTypes?.map((type: any) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Risk Level</Label>
                <Select value={filters.riskLevel || "all"} onValueChange={(value) => setFilters({ ...filters, riskLevel: value === "all" ? "" : value })}>
                  <SelectTrigger className="border-gray-300 w-full">
                    <SelectValue placeholder="All levels" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    <SelectItem value="all">All Levels</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Officer Name</Label>
                <Input
                  placeholder="Filter by officer..."
                  value={filters.officerName}
                  onChange={(e) => setFilters({ ...filters, officerName: e.target.value })}
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Act / Section</Label>
                <Input
                  placeholder="e.g. IPC 302..."
                  value={filters.actSection}
                  onChange={(e) => setFilters({ ...filters, actSection: e.target.value })}
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Address</Label>
                <Input
                  placeholder="Filter by address..."
                  value={filters.address}
                  onChange={(e) => setFilters({ ...filters, address: e.target.value })}
                  className="border-gray-300"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by name, father's name, GD number, case Category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-gray-300"
                />
              </div>
              <Button onClick={applyFilters} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
                Apply Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList className="grid grid-cols-4 bg-gray-100" style={{ minWidth: '600px' }}>
              <TabsTrigger value="top-suspects" className="data-[state=active]:bg-white">
                <TrendingUp className="h-4 w-4 mr-1" />
                Top Suspects ({topSuspects.length})
              </TabsTrigger>
              <TabsTrigger value="top-victims" className="data-[state=active]:bg-white">
                <TrendingUp className="h-4 w-4 mr-1" />
                Top Victims ({topVictims.length})
              </TabsTrigger>
              <TabsTrigger value="suspect" className="data-[state=active]:bg-white">
                Suspects ({stats.suspects})
              </TabsTrigger>
              <TabsTrigger value="victim" className="data-[state=active]:bg-white">
                Victims ({stats.victims})
              </TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2 ml-4">
              <Label className="text-sm text-gray-600 whitespace-nowrap">Min Cases</Label>
              <Input
                type="number"
                min={0}
                value={minCount === "" ? "" : minCount}
                onChange={(e) => setMinCount(e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))}
                placeholder="0"
                className="border-gray-300 w-20"
              />
              <Label className="text-sm text-gray-600 whitespace-nowrap">Show Top</Label>
              <Select value={String(topLimit)} onValueChange={(val) => setTopLimit(Number(val))}>
                <SelectTrigger className="border-gray-300 w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200">
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Top Suspects Tab */}
          <TabsContent value="top-suspects">
            <Card className="border border-gray-200">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg text-black flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Most Frequent Suspects ({topSuspects.length} suspects)
                    </CardTitle>
                    <p className="text-sm text-gray-600">
                      Suspects appearing in multiple cases, ranked by frequency of involvement
                    </p>
                  </div>
                  <Button
                    onClick={() => exportTopList("suspects")}
                    disabled={exportLoading}
                    variant="outline"
                    size="sm"
                    className="border-gray-300"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {exportLoading ? "Exporting..." : "Export Filtered"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {topSuspectsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                    <span className="ml-2 text-gray-600">Loading top suspects...</span>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="text-black font-semibold">Rank</TableHead>
                          <TableHead className="text-black font-semibold">Name</TableHead>
                          <TableHead className="text-black font-semibold">Father's Name</TableHead>
                          <TableHead className="text-black font-semibold">Case Count</TableHead>
                          <TableHead className="text-black font-semibold">Risk Level</TableHead>
                          <TableHead className="text-black font-semibold">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {topSuspects.map((suspect, index) => (
                          <TableRow key={`${suspect.name}-${index}`} className="hover:bg-gray-50">
                            <TableCell className="font-medium text-black">
                              <div className="flex items-center gap-2">
                                <div
                                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                    index === 0
                                      ? "bg-yellow-100 text-yellow-800"
                                      : index === 1
                                        ? "bg-gray-100 text-gray-800"
                                        : index === 2
                                          ? "bg-orange-100 text-orange-800"
                                          : "bg-blue-100 text-blue-800"
                                  }`}
                                >
                                  {index + 1}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="font-medium text-black">{suspect.name}</TableCell>
                            <TableCell className="text-gray-600">
                              {suspect.fatherName || <span className="italic text-gray-400">Not specified</span>}
                            </TableCell>
                            <TableCell className="text-gray-600">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="font-semibold">
                                  {suspect.count} cases
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell>{getRiskLevelBadge(suspect.count)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" className="hover:bg-gray-100 cursor-pointer" onClick={() => handleViewSuspect(suspect)}>
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Top Victims Tab */}
          <TabsContent value="top-victims">
            <Card className="border border-gray-200">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg text-black flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Most Frequent Victims ({topVictims.length} victims)
                    </CardTitle>
                    <p className="text-sm text-gray-600">
                      Victims appearing in multiple cases, ranked by frequency of involvement
                    </p>
                  </div>
                  <Button
                    onClick={() => exportTopList("victims")}
                    disabled={exportLoading}
                    variant="outline"
                    size="sm"
                    className="border-gray-300"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {exportLoading ? "Exporting..." : "Export Filtered"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {topVictimsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                    <span className="ml-2 text-gray-600">Loading top victims...</span>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="text-black font-semibold">Rank</TableHead>
                          <TableHead className="text-black font-semibold">Name</TableHead>
                          <TableHead className="text-black font-semibold">Father's Name</TableHead>
                          <TableHead className="text-black font-semibold">Case Count</TableHead>
                          <TableHead className="text-black font-semibold">Risk Level</TableHead>
                          <TableHead className="text-black font-semibold">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {topVictims.map((victim, index) => (
                          <TableRow key={`${victim.name}-${index}`} className="hover:bg-gray-50">
                            <TableCell className="font-medium text-black">
                              <div className="flex items-center gap-2">
                                <div
                                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                    index === 0
                                      ? "bg-yellow-100 text-yellow-800"
                                      : index === 1
                                        ? "bg-gray-100 text-gray-800"
                                        : index === 2
                                          ? "bg-orange-100 text-orange-800"
                                          : "bg-blue-100 text-blue-800"
                                  }`}
                                >
                                  {index + 1}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="font-medium text-black">{victim.name}</TableCell>
                            <TableCell className="text-gray-600">
                              {victim.fatherName || <span className="italic text-gray-400">Not specified</span>}
                            </TableCell>
                            <TableCell className="text-gray-600">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="font-semibold">
                                  {victim.count} cases
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell>{getRiskLevelBadge(victim.count)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" className="hover:bg-gray-100 cursor-pointer" onClick={() => handleViewSuspect(victim)}>
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Suspects Tab */}
          <TabsContent value="suspect">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <UserX className="h-5 w-5 text-red-600" />
                  Suspects ({suspectsPagination.total} total)
                  {suspectsPagination.loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black ml-2"></div>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-black font-semibold">Name</TableHead>
                        <TableHead className="text-black font-semibold">Father's Name</TableHead>
                        <TableHead className="text-black font-semibold">Type</TableHead>
                        <TableHead className="text-black font-semibold">GD Number</TableHead>
                        <TableHead className="text-black font-semibold">Date</TableHead>
                        <TableHead className="text-black font-semibold">Case Category</TableHead>
                        <TableHead className="text-black font-semibold">Police Station</TableHead>
                        <TableHead className="text-black font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSuspectsData.map((item) => (
                        <TableRow key={item.id} className="hover:bg-gray-50">
                          <TableCell className="font-medium text-black">{item.name}</TableCell>
                          <TableCell className="text-gray-600">{item.fatherName}</TableCell>
                          <TableCell>{getPersonTypeBadge(item.type)}</TableCell>
                          <TableCell className="text-gray-600">
                            <Button variant="link" className="p-0 h-auto text-blue-600 hover:text-blue-800">
                              {item.gdNumber}
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </Button>
                          </TableCell>
                          <TableCell className="text-gray-600">{new Date(item.date).toLocaleDateString('en-IN')}</TableCell>
                          <TableCell className="text-gray-600">{item.caseCategory}</TableCell>
                          <TableCell className="text-gray-600">{item.policeStation?.name || item.policeStation}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" className="hover:bg-gray-100" onClick={() => handleViewSuspect(item)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {renderPagination(suspectsPagination, handleSuspectsPageChange)}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Victims Tab */}
          <TabsContent value="victim">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <UserCheck className="h-5 w-5 text-blue-600" />
                  Victims ({victimsPagination.total} total)
                  {victimsPagination.loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black ml-2"></div>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-black font-semibold">Name</TableHead>
                        <TableHead className="text-black font-semibold">Father's Name</TableHead>
                        <TableHead className="text-black font-semibold">Type</TableHead>
                        <TableHead className="text-black font-semibold">GD Number</TableHead>
                        <TableHead className="text-black font-semibold">Date</TableHead>
                        <TableHead className="text-black font-semibold">Case Category</TableHead>
                        <TableHead className="text-black font-semibold">Police Station</TableHead>
                        <TableHead className="text-black font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredVictimsData.map((item) => (
                        <TableRow key={item.id} className="hover:bg-gray-50">
                          <TableCell className="font-medium text-black">{item.name}</TableCell>
                          <TableCell className="text-gray-600">{item.fatherName}</TableCell>
                          <TableCell>{getPersonTypeBadge(item.type)}</TableCell>
                          <TableCell className="text-gray-600">
                            <Button variant="link" className="p-0 h-auto text-blue-600 hover:text-blue-800">
                              {item.gdNumber}
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </Button>
                          </TableCell>
                          <TableCell className="text-gray-600">{new Date(item.date).toLocaleDateString('en-IN')}</TableCell>
                          <TableCell className="text-gray-600">{item.caseCategory}</TableCell>
                          <TableCell className="text-gray-600">{item.policeStation?.name || item.policeStation}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" className="hover:bg-gray-100" onClick={() => handleViewSuspect(item)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {renderPagination(victimsPagination, handleVictimsPageChange)}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Suspect Detail Popup */}
        <SuspectDetailPopup open={suspectDetailOpen} onOpenChange={setSuspectDetailOpen} suspect={selectedSuspect} />
      </div>
    </div>
  )
}